/**
 * useIsMobile — true when the viewport is at the app's MOBILE width.
 *
 * 2026-05-22 (Anil, Bug 1) — the app shell goes mobile at 860px (bottom
 * tabbar, no rail — see shell.css). The Leads screen needs the SAME
 * breakpoint in JS so it can render ONE in-flow pane at a time on a phone
 * (list ⇆ detail) instead of a 2-column master–detail with a floating
 * sticky triage pane. Keeping the 860px constant here means the CSS and
 * the JS can never drift apart.
 *
 * SSR / no-`matchMedia` safe: defaults to desktop (false) when `window`
 * is absent, and subscribes to the media query so an orientation change
 * or window resize updates the view live.
 */
import { useEffect, useState } from 'react';

/** The app's mobile breakpoint — kept identical to shell.css `max-width:860px`. */
export const MOBILE_MAX_WIDTH = 860;

const QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;

function readMatch(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(QUERY).matches;
}

/** React hook — re-renders the caller when crossing the mobile breakpoint. */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(readMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(QUERY);
    const onChange = () => setIsMobile(mql.matches);
    onChange(); // sync immediately in case the width changed before mount
    // addEventListener is the modern API; older Safari needs addListener.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isMobile;
}
